import { useEffect } from 'react';
import {
  AuditLogTags,
  getLogStore,
  startUploadCycle,
  type AuditSessionContext,
} from '@oolio-group/audit-sdk';
import useBehaviorSubjectState from '../hooks/rxjs/useSubjectState';
import { sessionSubject } from '../state/sessionObservable';
import { logCdsEventOnce } from './cdsAudit';

/**
 * CDS uploads under the paired POS terminal's device id, with "-CDS" appended to
 * the device name. That keeps both halves of a till in one place in the audit
 * service while still writing to separate files (the storage path is keyed on
 * device name + device id prefix).
 *
 * The audit service rejects an upload outright when any of the org/store/device
 * names is empty, so the cycle only starts once all six fields are present.
 */
export const useAuditUploadCycle = (): void => {
  const { value: session } = useBehaviorSubjectState(sessionSubject);

  const orgId = session?.currentOrganization?.id;
  const orgName = session?.currentOrganization?.name;
  const storeId = session?.currentStore?.id;
  const storeName = session?.currentStore?.name;
  const deviceId = session?.device?.id;
  const deviceName = session?.device?.name;

  useEffect(() => {
    const auditSession: AuditSessionContext | null =
      orgId && orgName && storeId && storeName && deviceId && deviceName
        ? {
            orgId,
            orgName,
            storeId,
            storeName,
            deviceId,
            deviceName: `${deviceName}-CDS`,
          }
        : null;

    if (!auditSession) {
      const missing = [
        !orgId && 'orgId',
        !orgName && 'orgName',
        !storeId && 'storeId',
        !storeName && 'storeName',
        !deviceId && 'deviceId',
        !deviceName && 'deviceName',
      ].filter(Boolean);
      logCdsEventOnce(`AUDIT_UPLOAD_SKIPPED:${missing.join(',')}`, {
        actionType: 'AUDIT_UPLOAD_SKIPPED',
        tag: AuditLogTags.SYSTEM,
        phrase: 'audit upload not started: session incomplete',
        level: 'warn',
        context: { missing },
      });
      return;
    }

    return startUploadCycle(getLogStore(), auditSession);
  }, [orgId, orgName, storeId, storeName, deviceId, deviceName]);
};
